'use client';

import React from 'react';
import { cn } from '@/lib/cn';
import Popover from '@/components/ui/Popover';
import { Button } from '@/components/ui/Button';
import { IconSearch, IconAiSparkles } from '@repo/svgs';
import { getListingsCount } from '@repo/api';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAiSearchController } from './hooks/useAiSearchController';
import { useRotatingPlaceholder } from './hooks/useRotatingPlaceholder';
import { AiSearchResults } from './components/AiSearchResults';
import { RotatingPlaceholder } from './components/RotatingPlaceholder';

export interface AISearchBarProps {
  className?: string;
  placeholder?: string;
  /** Focus the field on mount (e.g. opened from the mini bar's text tap). */
  autoFocus?: boolean;
}

/**
 * The AI-powered home search bar (desktop). A single free-text pill: the backend
 * turns the query into a listings-results URL on submit and we navigate there.
 * Focused-empty shows recent searches or the backend's starter prompts.
 *
 * The trailing "Search {n} listings" pill mirrors the legacy bar's submit button:
 * the count is the shared listings-count from the filters store (same source /
 * same number the legacy bar shows). Clicking it submits the typed query (or, when
 * empty, navigates to the full listings results — matching the legacy button).
 *
 * Gated by `useAiSearchStatus` upstream (see `HomeAiSearch`), so this only
 * mounts when the AI bar is enabled.
 */
export function AISearchBar({
  className,
  placeholder = 'What are you looking for?',
  autoFocus = false,
}: AISearchBarProps) {
  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const analytics = useAnalytics();

  const {
    query,
    setQuery,
    isSubmitting,
    suggestions,
    recents,
    refreshRecents,
    submitTyped,
    goToUrl,
  } = useAiSearchController(true);

  // Auto-grow the field so long queries wrap onto multiple lines (#8), capped so
  // the bar doesn't grow unbounded (then it scrolls internally).
  const autosize = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);

  React.useEffect(() => {
    autosize();
  }, [query, autosize]);

  React.useEffect(() => {
    if (!autoFocus) return;
    // The bar mounts inside the header overlay's slide-down animation
    // (`--animate-slide-down`, 0.3s). Focusing mid-transition makes the browser
    // scroll toward a still-moving target, so wait for it to settle first.
    const id = window.setTimeout(() => {
      textareaRef.current?.focus({ preventScroll: true });
    }, 300);
    return () => window.clearTimeout(id);
  }, [autoFocus]);

  // The "default" state of the home AI bar is the verified-listings landing
  // (same as the footer's `/listings/status:verified`). We fetch that count
  // directly rather than reading persisted filter state from the store, so the
  // button never reflects a user's previously-saved filters (#11).
  const [listingsCount, setListingsCount] = React.useState<number | null>(null);

  React.useEffect(() => {
    let active = true;
    getListingsCount({ filters: { isVerified: true } })
      .then(res => {
        if (active) setListingsCount(res.count);
      })
      .catch(() => {
        /* leave count hidden on failure */
      });
    return () => {
      active = false;
    };
  }, []);

  const rotatingPlaceholder = useRotatingPlaceholder(placeholder, suggestions, query.length === 0);

  // Only surface the dropdown while the field is empty — recents (if any) or
  // the starter suggestions. Once the user starts typing, hide it (#13).
  const hasDropdownContent =
    query.trim().length === 0 && (recents.length > 0 || suggestions.length > 0);

  React.useEffect(() => {
    setOpen(isFocused && !isSubmitting && hasDropdownContent);
  }, [isFocused, isSubmitting, hasDropdownContent]);

  const handleFocus = React.useCallback(() => {
    setIsFocused(true);
    refreshRecents();
    analytics.track('header_listings_ai_text');
  }, [refreshRecents, analytics]);

  const handleBlur = React.useCallback(() => {
    // Defer so a click inside the popover (ListCell isn't focusable) still fires.
    window.setTimeout(() => setIsFocused(false), 0);
  }, []);

  // Every submit route lands here (search button and Enter), so the event is
  // tracked once here rather than per-route.
  const handleSubmit = React.useCallback(() => {
    // The buttons self-disable while submitting; Enter needs the same guard so a
    // second press can't re-navigate or double-count the event.
    if (isSubmitting) return;
    setOpen(false);
    analytics.track('header_listings_ai_search');
    if (query.trim().length > 0) {
      submitTyped();
    } else {
      // Empty query → verified-listings default (not persisted/saved filters) (#11, #17).
      goToUrl('/listings/status:verified');
    }
  }, [isSubmitting, analytics, query, submitTyped, goToUrl]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter submits; Shift+Enter inserts a newline.
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  // Show the count only when the field is empty and a count is available.
  // Once the user has typed (even after blurring) we hide the count entirely
  // so the button keeps a constant height / icon size (#7, #10).
  const showCount = query.trim().length === 0 && listingsCount !== null;
  const countLabel = `Search ${
    listingsCount !== null ? listingsCount.toLocaleString() : ''
  } listings`;

  return (
    <>
      <div
        ref={anchorRef}
        role="search"
        className={cn(
          // 8px inset on top/right/bottom (#4); sparkle sits 16px from the left (#5, pl-4).
          // The field grows over multiple lines (#8); the sparkle stays vertically
          // centered with the text and the trailing button drops to the bottom.
          'flex w-full max-w-[600px] items-start gap-2 rounded-4xl border border-secondary bg-primary py-2 pl-4 pr-2',
          'min-h-[60px] transition-shadow',
          isFocused ? 'shadow-none' : 'shadow-lg hover:bg-secondary',
          className,
        )}
      >
        <IconAiSparkles className="w-5 h-5 icon-tertiary shrink-0 self-center" />
        <div className="relative min-w-0 flex-1">
          <textarea
            ref={textareaRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onInput={autosize}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            readOnly={isSubmitting}
            rows={1}
            autoComplete="off"
            aria-label="Search apartments with AI"
            className="block w-full resize-none bg-transparent py-2.5 leading-6 text-body-2 text-primary outline-none"
          />
          <RotatingPlaceholder
            text={rotatingPlaceholder}
            hidden={query.length > 0}
            className="py-2.5 leading-6 text-body-2"
          />
        </div>
        {!isFocused && showCount ? (
          // Idle + empty + count available: show the "Search {count} listings" pill.
          <Button
            onClick={handleSubmit}
            variant="primary"
            size="sm"
            isLoading={isSubmitting}
            iconLeft={!isSubmitting ? <IconSearch className="w-4 h-4" /> : undefined}
            className="h-11 shrink-0 self-end"
          >
            <span className="text-body-2-high">{countLabel}</span>
          </Button>
        ) : (
          // Focused or already typing: constant-size circular search-icon button (#7).
          <Button
            onClick={handleSubmit}
            variant="primary"
            size="sm"
            isLoading={isSubmitting}
            aria-label="Search"
            className="flex h-11 w-11 shrink-0 items-center justify-center self-end !p-0"
          >
            {!isSubmitting && <IconSearch className="w-4 h-4" />}
          </Button>
        )}
      </div>

      <Popover
        open={open}
        onOpenChange={o => setOpen(o)}
        anchorRef={anchorRef as React.RefObject<HTMLElement>}
        matchAnchorWidth
      >
        <div
          // Prevent input blur when clicking a row, so ListCell clicks fire.
          onMouseDown={e => e.preventDefault()}
          className="rounded-4xl bg-primary border border-tertiary shadow-lg p-4 overflow-hidden"
        >
          <AiSearchResults
            query={query}
            suggestions={suggestions}
            recents={recents}
            onSuggestion={s => {
              setOpen(false);
              analytics.track('header_listings_ai_suggestion');
              // Suggestions are pre-resolved; navigate straight to the URL.
              goToUrl(s.url, s.text);
            }}
            onRecent={r => {
              setOpen(false);
              analytics.track('header_listings_ai_recent');
              goToUrl(r.url, r.query);
            }}
          />
        </div>
      </Popover>
    </>
  );
}

export default AISearchBar;
